import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { assetsCollection, brandKitDoc } from './firestoreRefs';
import BusinessCardPreview from './BusinessCardPreview';
import { exportBusinessCardPdf } from './businessCardExport';
import LetterheadPrintable from './LetterheadPrintable';
import QRPosterPrintable from './QRPosterPrintable';
import EmailSignaturePrintable from './EmailSignaturePrintable';
import WhatsAppDPPrintable from './WhatsAppDPPrintable';
import QRStandeePrintable from './QRStandeePrintable';
import SaleRentFlyerPrintable from './SaleRentFlyerPrintable';
import WelcomeOnePagerPrintable from './WelcomeOnePagerPrintable';
import './Printables.css';
import './printablesStyles.css';

const DEFAULT_DESIGNATION = 'Senior Property Advisor';
const DEFAULT_PRINTABLE_DESIGNATION = 'Property Consultant';
const FALLBACK_LOGO_TEXT = 'Douglas & McCartney';
const BUSINESS_CARD_LOGO_URL = '/d_logo-removebg-preview.png';

const DEFAULT_COLORS = {
  primary: '#0a0a0a',
  secondary: '#f8f8f8',
  accent: '#c8a951',
  background: '#0c0c0c',
};

const DEFAULT_COMPLIANCE = {
  requireRera: true,
  requirePhone: true,
  requireAddress: true,
};

const PRINTABLE_TABS = [
  { key: 'letterhead', label: 'Letterhead' },
  { key: 'qr-poster', label: 'QR Poster', comingSoon: true },
  { key: 'email-signature', label: 'Email Signature' },
  { key: 'whatsapp-dp', label: 'Profile Picture' },
  { key: 'qr-standee', label: 'QR Standee', comingSoon: true },
  { key: 'sale-rent', label: 'Sale/Rent Flyer', comingSoon: true },
  { key: 'welcome-onepager', label: 'Welcome One-Pager', comingSoon: true },
];
const ORDERED_PRINTABLE_TABS = [
  ...PRINTABLE_TABS.filter((tab) => !tab.comingSoon),
  ...PRINTABLE_TABS.filter((tab) => tab.comingSoon),
];

const normalizeString = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
};

const resolveEmailPrefix = (value) => {
  const email = normalizeString(value);
  if (!email) return null;
  const parts = email.split('@');
  return parts[0] ? parts[0].trim() : null;
};

const resolveFullName = ({ userDoc, onboardingRequest, agreement, authUser }) => {
  const emailPrefix =
    resolveEmailPrefix(authUser?.email) ||
    resolveEmailPrefix(onboardingRequest?.assignedEmail);

  return (
    normalizeString(userDoc?.fullName) ||
    normalizeString(onboardingRequest?.fullName) ||
    normalizeString(agreement?.formData?.fullName) ||
    normalizeString(authUser?.displayName) ||
    normalizeString(authUser?.username) ||
    emailPrefix ||
    null
  );
};

const resolveDesignation = ({ userDoc, agreement }) =>
  normalizeString(userDoc?.designation) ||
  normalizeString(agreement?.formData?.designation) ||
  DEFAULT_DESIGNATION;

const resolvePrintableDesignation = ({ userDoc, agreement, city }) => {
  const resolved =
    normalizeString(userDoc?.designation) ||
    normalizeString(agreement?.formData?.designation);
  if (resolved) return resolved;
  if (city) {
    return `Managing Partner - ${city}`;
  }
  return DEFAULT_PRINTABLE_DESIGNATION;
};

const resolvePhone = ({ userDoc, onboardingRequest, agreement }) =>
  normalizeString(userDoc?.phoneNumber) ||
  normalizeString(userDoc?.phone) ||
  normalizeString(onboardingRequest?.phone) ||
  normalizeString(agreement?.formData?.phone) ||
  null;

const resolveAssignedEmail = ({ authUser, onboardingRequest }) =>
  normalizeString(authUser?.email) ||
  normalizeString(onboardingRequest?.assignedEmail) ||
  null;

const normalizeSocialLinks = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return {
    instagram: normalizeString(value.instagram),
    facebook: normalizeString(value.facebook),
    youtube: normalizeString(value.youtube),
    website: normalizeString(value.website),
  };
};

const normalizeBrandKit = (doc) => {
  const complianceSource = doc?.compliance || doc?.toggles || {};
  return {
    logoUrl: doc?.logoUrl || '',
    watermarkUrl: doc?.watermarkUrl || '',
    colors: {
      primary: doc?.colors?.primary || DEFAULT_COLORS.primary,
      secondary: doc?.colors?.secondary || DEFAULT_COLORS.secondary,
      accent: doc?.colors?.accent || DEFAULT_COLORS.accent,
      background: doc?.colors?.background || DEFAULT_COLORS.background,
    },
    compliance: {
      requireRera:
        typeof complianceSource.requireRera === 'boolean'
          ? complianceSource.requireRera
          : DEFAULT_COMPLIANCE.requireRera,
      requirePhone:
        typeof complianceSource.requirePhone === 'boolean'
          ? complianceSource.requirePhone
          : DEFAULT_COMPLIANCE.requirePhone,
      requireAddress:
        typeof complianceSource.requireAddress === 'boolean'
          ? complianceSource.requireAddress
          : DEFAULT_COMPLIANCE.requireAddress,
    },
    disclaimerText: doc?.disclaimerText || '',
  };
};

const Printables = ({
  firebase,
  authUser,
  brandingContext,
  linkedProfile,
  linkedRequest,
  agreement,
}) => {
  const history = useHistory();
  const frontCardRef = useRef(null);
  const backCardRef = useRef(null);
  const logoUrl = BUSINESS_CARD_LOGO_URL;
  const [saveToAssets, setSaveToAssets] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportSuccess, setExportSuccess] = useState('');
  const [uploadWarning, setUploadWarning] = useState('');
  const [activeTab, setActiveTab] = useState(() => PRINTABLE_TABS[0].key);
  const [brandKitDocState, setBrandKitDocState] = useState(null);
  const [brandKitLoading, setBrandKitLoading] = useState(false);
  const [brandKitError, setBrandKitError] = useState('');

  const tenantId = brandingContext?.tenantId || null;
  const branchId = brandingContext?.branchId || null;

  useEffect(() => {
    if (!tenantId) {
      setBrandKitDocState(null);
      setBrandKitLoading(false);
      setBrandKitError('');
      return;
    }

    setBrandKitLoading(true);
    setBrandKitError('');
    const unsubscribe = brandKitDoc(tenantId).onSnapshot(
      (snapshot) => {
        setBrandKitDocState(snapshot.exists ? { id: snapshot.id, ...snapshot.data() } : null);
        setBrandKitLoading(false);
      },
      (error) => {
        console.error('Brand kit load failed:', error);
        setBrandKitError('Unable to load brand kit details.');
        setBrandKitLoading(false);
      },
    );

    return () => unsubscribe();
  }, [tenantId]);

  const brandKit = useMemo(() => normalizeBrandKit(brandKitDocState), [brandKitDocState]);

  const resolvedData = useMemo(() => {
    const fullName = resolveFullName({
      userDoc: linkedProfile,
      onboardingRequest: linkedRequest,
      agreement,
      authUser,
    });
    const designation = resolveDesignation({
      userDoc: linkedProfile,
      agreement,
    });
    const phone = resolvePhone({
      userDoc: linkedProfile,
      onboardingRequest: linkedRequest,
      agreement,
    });
    const assignedEmail = resolveAssignedEmail({
      authUser,
      onboardingRequest: linkedRequest,
    });

    return {
      fullName,
      designation,
      phone,
      assignedEmail,
    };
  }, [agreement, authUser, linkedProfile, linkedRequest]);

  const assignedEmailMissing = !normalizeString(linkedRequest?.assignedEmail);

  const branchProfile = useMemo(() => {
    const source = brandingContext?.branchProfile || {};
    const addressRaw = normalizeString(source.address);
    const address = addressRaw && addressRaw.toLowerCase() !== 'not set' ? addressRaw : null;
    const city =
      normalizeString(source.city) ||
      normalizeString(agreement?.formData?.city) ||
      normalizeString(agreement?.formData?.preferredLocation) ||
      null;
    const branchName =
      normalizeString(source.branchName) ||
      normalizeString(agreement?.formData?.companyName) ||
      normalizeString(agreement?.formData?.businessName) ||
      null;
    const reraId =
      normalizeString(source.reraId) ||
      normalizeString(agreement?.formData?.reraNumber) ||
      normalizeString(linkedProfile?.licenseNumber) ||
      null;
    const branchPhone = normalizeString(source.branchPhone) || null;
    const website =
      normalizeString(source.website) ||
      normalizeString(linkedProfile?.socialLinks?.website) ||
      null;

    return {
      branchName,
      address,
      city,
      reraId,
      branchPhone,
      website,
    };
  }, [agreement, brandingContext?.branchProfile, linkedProfile?.licenseNumber, linkedProfile?.socialLinks?.website]);

  const printableProfile = useMemo(() => {
    const fullName = resolveFullName({
      userDoc: linkedProfile,
      onboardingRequest: linkedRequest,
      agreement,
      authUser,
    });
    const phone = resolvePhone({
      userDoc: linkedProfile,
      onboardingRequest: linkedRequest,
      agreement,
    });
    const designation = resolvePrintableDesignation({
      userDoc: linkedProfile,
      agreement,
      city: branchProfile.city,
    });
    const whatsapp = normalizeString(linkedProfile?.whatsapp) || phone || null;
    const assignedEmail = resolveAssignedEmail({
      authUser,
      onboardingRequest: linkedRequest,
    });
    const socialLinks = normalizeSocialLinks(linkedProfile?.socialLinks);
    const photoUrl = normalizeString(linkedProfile?.photoUrl) || normalizeString(authUser?.photoURL) || null;

    return {
      fullName,
      designation,
      phone,
      whatsapp,
      email: assignedEmail,
      photoUrl,
      socialLinks,
    };
  }, [agreement, authUser, branchProfile.city, linkedProfile, linkedRequest]);

  const phoneMissing = !resolvedData.phone;

  const statusLabel = phoneMissing
    ? 'Missing Phone'
    : assignedEmailMissing
      ? 'Assigned Email not set'
      : 'Ready';

  const statusTone = phoneMissing
    ? 'warning'
    : assignedEmailMissing
      ? 'info'
      : 'success';

  const destinationUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const origin = window.location.origin || '';
    return origin ? `${origin}/franchise-dashboard` : '/franchise-dashboard';
  }, []);
  const activeTabConfig = useMemo(
    () => PRINTABLE_TABS.find((tab) => tab.key === activeTab) || null,
    [activeTab],
  );
  const isComingSoonTab = Boolean(activeTabConfig?.comingSoon);

  const handleExport = async () => {
    if (phoneMissing) {
      return;
    }

    setExportError('');
    setExportSuccess('');
    setUploadWarning('');
    setExporting(true);

    try {
      const fileName = `Douglas-McCartney-Business-Card-${(resolvedData.fullName || 'franchisee')
        .replace(/[^a-zA-Z0-9-]+/g, '-')
        .toLowerCase()}.pdf`;
      const pdfBlob = await exportBusinessCardPdf({
        frontNode: frontCardRef.current,
        backNode: backCardRef.current,
        fileName,
      });
      setExportSuccess('Business card downloaded.');

      if (saveToAssets) {
        if (!tenantId || !branchId || !authUser?.uid) {
          setUploadWarning('Upload failed, file not saved.');
        } else {
          let assetRef = null;
          try {
            assetRef = assetsCollection(tenantId).doc();
            const now = firebase.fieldValue.serverTimestamp();
            const snapshot = {
              fullName: resolvedData.fullName || '',
              designation: resolvedData.designation || DEFAULT_DESIGNATION,
              phone: resolvedData.phone || '',
              assignedEmail: resolvedData.assignedEmail || '',
            };

            await assetRef.set({
              type: 'business_card',
              tenantId,
              branchId,
              createdBy: authUser.uid,
              createdAt: now,
              updatedAt: now,
              status: 'processing',
              snapshot,
            });

            const storagePath = `tenants/${tenantId}/assets/${assetRef.id}/business-card.pdf`;
            const metadata = {
              contentType: 'application/pdf',
              customMetadata: {
                tenantId,
                branchId,
                createdBy: authUser.uid,
              },
            };
            const uploadTask = await firebase.storage.ref().child(storagePath).put(pdfBlob, metadata);
            const pdfUrl = await uploadTask.ref.getDownloadURL();

            await assetRef.set(
              {
                status: 'ready',
                updatedAt: firebase.fieldValue.serverTimestamp(),
                output: { pdfUrl, storagePath },
              },
              { merge: true },
            );
          } catch (error) {
            console.error('Asset upload failed:', error);
            setUploadWarning('Upload failed, file not saved.');
            if (assetRef) {
              try {
                await assetRef.set(
                  {
                    status: 'failed',
                    updatedAt: firebase.fieldValue.serverTimestamp(),
                    errorMessage: error.message || 'Upload failed',
                  },
                  { merge: true },
                );
              } catch (secondaryError) {
                console.error('Failed to update asset failure status:', secondaryError);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Business card export failed:', error);
      setExportError('Could not generate PDF. Try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="printables-page">
      <header className="printables-header">
        <div>
          <h2>Printables</h2>
          <p>Download your official branded materials.</p>
        </div>
      </header>

      {(exportError || exportSuccess || uploadWarning) && (
        <div className="printables-banners">
          {exportError && <div className="printables-banner error">{exportError}</div>}
          {exportSuccess && <div className="printables-banner success">{exportSuccess}</div>}
          {uploadWarning && <div className="printables-banner warning">{uploadWarning}</div>}
        </div>
      )}

      {assignedEmailMissing && (
        <div className="printables-banner warning">Assigned email not set yet.</div>
      )}

      <section className="printables-card">
        <div className="printables-card-header">
          <h3>Business Card</h3>
          <span className={`printables-status ${statusTone}`}>{statusLabel}</span>
        </div>

        <div className="printables-grid">
          <div className="printables-preview">
            <div className="printables-preview-stack">
              <div className="printables-preview-block">
                <span className="printables-preview-label">Front</span>
                <div className="printables-preview-frame">
                  <BusinessCardPreview
                    ref={frontCardRef}
                    variant="front"
                    logoUrl={logoUrl}
                    fullName={resolvedData.fullName}
                    designation={resolvedData.designation}
                    phone={resolvedData.phone}
                    email={resolvedData.assignedEmail}
                    fallbackLogoText={FALLBACK_LOGO_TEXT}
                  />
                </div>
              </div>
              <div className="printables-preview-block">
                <span className="printables-preview-label">Back</span>
                <div className="printables-preview-frame">
                  <BusinessCardPreview
                    ref={backCardRef}
                    variant="back"
                    logoUrl={logoUrl}
                    fullName={resolvedData.fullName}
                    designation={resolvedData.designation}
                    phone={resolvedData.phone}
                    email={resolvedData.assignedEmail}
                    fallbackLogoText={FALLBACK_LOGO_TEXT}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="printables-details">
            <div className="printables-details-card">
              <h4>Card Details</h4>
              <div className="printables-detail">
                <span>Name</span>
                <strong>{resolvedData.fullName || 'Not set'}</strong>
              </div>
              <div className="printables-detail">
                <span>Designation</span>
                <strong>{resolvedData.designation || DEFAULT_DESIGNATION}</strong>
              </div>
              <div className="printables-detail">
                <span>Phone</span>
                <strong>{resolvedData.phone || 'Add phone in Profile'}</strong>
              </div>
              <div className="printables-detail">
                <span>Assigned Email</span>
                <strong>{resolvedData.assignedEmail || 'Not set'}</strong>
              </div>
            </div>

            {phoneMissing && (
              <div className="printables-banner warning">
                Add your phone number in Profile to download.
                <button
                  type="button"
                  className="printables-link"
                  onClick={() => history.push('/account')}
                >
                  Go to Profile
                </button>
              </div>
            )}

            <div className="printables-actions">
              <label className="printables-checkbox">
                <input
                  type="checkbox"
                  checked={saveToAssets}
                  onChange={(event) => setSaveToAssets(event.target.checked)}
                />
                Save to Assets Library
              </label>
              <button
                type="button"
                className="printables-download"
                onClick={handleExport}
                disabled={exporting || phoneMissing}
              >
                {exporting ? 'Generating PDF...' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="printables-card">
        <div className="printables-card-header">
          <h3>More Printables</h3>
          <span className="printables-status info">Branding Suite</span>
        </div>

        {brandKitError && <div className="printables-banner error">{brandKitError}</div>}
        {!brandKitDocState && !brandKitLoading && (
          <div className="printables-banner warning">
            Brand kit assets are not configured yet. Using defaults.
          </div>
        )}

        <div className="printables-tabs">
          {ORDERED_PRINTABLE_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`printables-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.comingSoon ? `${tab.label} (Coming Soon)` : tab.label}
            </button>
          ))}
        </div>

        <div className="printables-module">
          {isComingSoonTab ? (
            <div className="printables-banner info">
              {activeTabConfig?.label || 'This printable'} is coming soon.
            </div>
          ) : (
            <>
              {activeTab === 'letterhead' && (
                <LetterheadPrintable
                  firebase={firebase}
                  authUser={authUser}
                  tenantId={tenantId}
                  branchId={branchId}
                  profile={printableProfile}
                  branch={branchProfile}
                  brandKit={brandKit}
                  assignedEmail={resolvedData.assignedEmail}
                  assignedEmailMissing={assignedEmailMissing}
                  compliance={brandKit.compliance}
                />
              )}
              {activeTab === 'qr-poster' && (
                <QRPosterPrintable
                  firebase={firebase}
                  authUser={authUser}
                  tenantId={tenantId}
                  branchId={branchId}
                  profile={printableProfile}
                  branch={branchProfile}
                  brandKit={brandKit}
                  assignedEmail={resolvedData.assignedEmail}
                  assignedEmailMissing={assignedEmailMissing}
                  compliance={brandKit.compliance}
                />
              )}
              {activeTab === 'email-signature' && (
                <EmailSignaturePrintable
                  firebase={firebase}
                  authUser={authUser}
                  tenantId={tenantId}
                  branchId={branchId}
                  profile={printableProfile}
                  branch={branchProfile}
                  brandKit={brandKit}
                  assignedEmail={resolvedData.assignedEmail}
                  assignedEmailMissing={assignedEmailMissing}
                  compliance={brandKit.compliance}
                />
              )}
              {activeTab === 'whatsapp-dp' && (
                <WhatsAppDPPrintable
                  firebase={firebase}
                  authUser={authUser}
                  tenantId={tenantId}
                  branchId={branchId}
                  profile={printableProfile}
                  branch={branchProfile}
                  brandKit={brandKit}
                  assignedEmail={resolvedData.assignedEmail}
                  assignedEmailMissing={assignedEmailMissing}
                />
              )}
              {activeTab === 'qr-standee' && (
                <QRStandeePrintable
                  firebase={firebase}
                  authUser={authUser}
                  tenantId={tenantId}
                  branchId={branchId}
                  profile={printableProfile}
                  branch={branchProfile}
                  brandKit={brandKit}
                  assignedEmail={resolvedData.assignedEmail}
                  assignedEmailMissing={assignedEmailMissing}
                  compliance={brandKit.compliance}
                />
              )}
              {activeTab === 'sale-rent' && (
                <SaleRentFlyerPrintable
                  firebase={firebase}
                  authUser={authUser}
                  tenantId={tenantId}
                  branchId={branchId}
                  profile={printableProfile}
                  branch={branchProfile}
                  brandKit={brandKit}
                  assignedEmail={resolvedData.assignedEmail}
                  assignedEmailMissing={assignedEmailMissing}
                  compliance={brandKit.compliance}
                />
              )}
              {activeTab === 'welcome-onepager' && (
                <WelcomeOnePagerPrintable
                  firebase={firebase}
                  authUser={authUser}
                  tenantId={tenantId}
                  branchId={branchId}
                  profile={printableProfile}
                  branch={branchProfile}
                  brandKit={brandKit}
                  assignedEmail={resolvedData.assignedEmail}
                  assignedEmailMissing={assignedEmailMissing}
                  compliance={brandKit.compliance}
                  destinationUrl={destinationUrl}
                />
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Printables;
